<script setup lang="ts">
import { DatePicker } from 'v-calendar';
// import 'v-calendar/style.css';

import { faCalendarDay, faTimes } from '@fortawesome/pro-regular-svg-icons';
// validation
import { defineRule, useForm, useField } from 'vee-validate';
import { email, required } from '@vee-validate/rules';

import { languages } from '@/locales/fixtures';
import links from '@/assets/json/footer.json';

// utils
import dayjs from '@/utils/day';
import { scrollToElement } from '@/utils/general';

// components
import CiModalMail from '@/components/CiModal/CiModalMail.vue';
import CiSkeletonInquire from '@/components/skeleton/CiSkeletonInquire.vue';

// validation rules
defineRule('email', email);
defineRule('required', required);

// constants
const USER_STATUS_ANONYMOUS = 'anonymous';
const USER_STATUS_UNVERIFIED = 'unverified';
const USER_STATUS_VERIFIED = 'verified';

/********************
 * COMPOSITIONS      *
 ********************/
const campsiteStore = useCampsiteStore();
const inquireService = useInquireService();
const localePath = useLocalePath();
const route = useRoute();
const router = useRouter();
const seoStore = useSeoStore();
const userStore = useUserStore();
const viewport = useViewport();
const { $gettext, $pgettext } = useGettext();
const { fetchError, fetchFailed, fetchStatus } = useFetchError();
const { locale } = useI18n();
const { openModal, closeModal } = useCiModal();
const { i18nMarketingSignupLabel } = useI18nMeta();

/********************
 * INITIALIZATION    *
 ********************/
const { pending, error: dataError } = await useLazyAsyncData(`inquire-${route.params.identifier}`, async () => {
  const campsite = campsiteStore.campsite;

  try {
    if (!campsite || campsite.slug !== route.params.identifier) {
      const campsite = await campsiteStore.getCampsite({ slug: route.params.identifier, lang: locale.value });
      return campsite;
    }
    return campsite;
  } catch (error) {
    fetchError(error);
    return error;
  }
});

if (dataError.value) {
  throw createError({ statusCode: dataError.value?.statusCode, message: dataError.value.statusMessage });
}

/********************
 * REFS & VARS       *
 ********************/
const datePickerJumpDate = ref(null);
const rootRef = ref<HTMLElement | null>(null);
const range = ref<{ start: Date | null; end: Date | null }>({ start: null, end: null });
const error = ref(null);
const submitPending = ref(false);
const inquire = reactive({
  campsite: null,
  date_from: null,
  date_until: null,
  desired_languages: [],
  email: null,
  first_name: null,
  last_name: null,
  message: '',
  marketing_signup: false,
  insider_uid: userStore.insiderUid,
});

// form
const { validate } = useForm({
  validationSchema: {
    email: 'required|email',
    firstName: 'required',
    lastName: 'required',
    message: 'required',
    marketingSignup: '',
  },
});

const { value: emailValue, errorMessage: emailError } = useField<string>('email');
const { value: firstNameValue, errorMessage: firstNameError } = useField<string>('firstName');
const { value: lastNameValue, errorMessage: lastNameError } = useField<string>('lastName');
const { value: messageValue, errorMessage: messageError } = useField<string>('message');
const { value: marketingSignupValue } = useField<boolean>('marketingSignup');

const i18nInquireCopy = computed(() => {
  return $gettext(
    'Send your non-binding inquiry here. You will receive an answer by e-mail from the campsite. This request is not yet a booking and you will not incur any costs.',
  );
});

const i18PrivacyPolicyLink = computed(() => {
  const text = $pgettext('Privacy Policy Link Text', 'Privacy Policy');
  const link = router.resolve(
    localePath({
      name: 'info-all',
      params: {
        all: links.footer.privacy[locale.value] || links.footer.privacy.en || links.footer.privacy.de || null,
      },
    }),
  );
  return `<a href="${link.href}" class="no-wrap" target="_blank" rel="noopener">${text}</a>`;
});

const i18nTermsOfUseLink = computed(() => {
  const text = $pgettext('Terms and Conditions B2C Link Text', 'Terms and Conditions B2C');
  const link = router.resolve(
    localePath({
      name: 'info-all',
      params: {
        all: links.termsOfServiceB2C[locale.value] || links.termsOfServiceB2C.en || links.termsOfServiceB2C.de || null,
      },
    }),
  );
  return `<a href="${link.href}" class="no-wrap" target="_blank" rel="noopener">${text}</a>`;
});

/********************
 * WATCHER           *
 ********************/
watch(() => campsiteStore.campsite, onCampsiteChange, { immediate: true });
watch(() => userStore.user.profile, setDefaultValues, { immediate: true });

/********************
 * FUNCTIONS         *
 ********************/
function onCampsiteChange(newValue) {
  if (newValue) {
    if (!newValue.meta.inquiries_allowed) {
      return navigateTo(localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: newValue.slug } }));
    }

    if (route.query.from && route.query.until) {
      range.value = {
        start: new Date(Date.parse(route.query.from as string)),
        end: new Date(Date.parse(route.query.until as string)),
      };
      onDateSelect({
        start: new Date(Date.parse(route.query.from as string)),
        end: new Date(Date.parse(route.query.until as string)),
      });
    }

    const parsedId = parseInt(route.params?.identifier as string, 10) || null;

    if (parsedId && route.params.identifier !== newValue.slug) {
      return navigateTo(localePath({ name: route.name || undefined, params: { identifier: newValue.slug } }), {
        replace: true,
      });
    }
    seoStore.setDynamicMetaParam({ campsite_name: newValue.meta.name });
    inquire.campsite = newValue.id;
  }
}

function getLanguage(lang) {
  const language = languages.find(l => l.code === lang);
  return language ? $gettext(language.name) : '';
}

function onDateSelect(value) {
  if (!value || !value.start || !value.end) {
    range.value = { start: null, end: null };
    inquire.date_from = null;
    inquire.date_until = null;
    return;
  }

  inquire.date_from = dayjs(value.start).format('YYYY-MM-DD');
  inquire.date_until = dayjs(value.end).format('YYYY-MM-DD');
}

function onLanguageSelected(value, lang) {
  const desiredLanguages = inquire.desired_languages;
  const isCheck = value; // $event.target.checked;

  if (isCheck && !desiredLanguages.includes(lang)) {
    desiredLanguages.push(lang);
  } else {
    inquire.desired_languages = desiredLanguages.filter(l => l !== lang);
  }
}

function onCancel() {
  return navigateTo(localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } }));
}

function scrollToError() {
  nextTick(() => {
    scrollToElement(rootRef.value, 'has-error');
  });
}

function setDefaultValues() {
  if (!userStore.user.profile) {
    return;
  }

  if (userStore.user.profile.first_name) {
    firstNameValue.value = userStore.user.profile.first_name;
  }

  if (userStore.user.profile.last_name) {
    lastNameValue.value = userStore.user.profile.last_name;
  }

  if (userStore.user.profile.email) {
    emailValue.value = userStore.user.profile.email;
  }
}

async function onSubmit() {
  submitPending.value = true;

  // validate
  const { valid } = await validate();
  if (!valid) {
    submitPending.value = false;
    scrollToError();
    return false;
  }

  if (!inquire.campsite) {
    inquire.campsite = campsiteStore.campsite?.id;
  }

  inquire.email = emailValue.value;
  inquire.first_name = firstNameValue.value;
  inquire.last_name = lastNameValue.value;
  inquire.message = messageValue.value;
  inquire.marketing_signup = marketingSignupValue.value;

  inquireService
    .create(locale.value, { ...toRaw(inquire) })
    .then((response) => {
      const userStatus = response.user_status;
      if (userStatus === USER_STATUS_UNVERIFIED || userStatus === USER_STATUS_ANONYMOUS) {
        // case user needs to confirm
        const buttons = [
          {
            label: $gettext('Back to campsite'),
            to: localePath({
              name: RBN_CAMPSITE_DETAIL,
              params: { identifier: campsiteStore.campsite?.slug },
            }),
            classes: ['button--primary'],
          },
        ];

        openModal(
          {
            component: CiModalMail,
            attrs: {
              buttons,
              headline: $gettext('Only one more step'),
              successMessage: $gettext(
                'You will receive an e-mail to confirm your inquiry. Please click on the link in the e-mail to complete your request.',
              ),
              onClose: () => {
                closeModal('CiMapModal');
              },
            },
          },
          {
            clickToClose: false,
            contentTransition: 'translate-y-down',
            contentClass: 'max-w-[800px] w-full',
          },
        );
      } else if (userStatus === USER_STATUS_VERIFIED) {
        return navigateTo(
          localePath({
            name: 'campsite-identifier-inquire-confirm',
            params: route.params,
            query: { verified: 1, from: response.date_from, until: response.date_until },
          }),
        );
      }
    })
    .catch((error) => {
      error.value = error;
    })
    .finally(() => {
      submitPending.value = false;
    });
}

function onMarketingSignupClicked(payload: boolean) {
  marketingSignupValue.value = payload;
}
</script>

<template>
  <div
    ref="rootRef"
    class="inquire"
  >
    <template v-if="!fetchFailed">
      <div class="bg-gray-10">
        <div class="container py-2">
          <CiNavigationToggle
            type="black"
            :href="
              localePath({
                name: RBN_CAMPSITE_DETAIL,
                params: { identifier: route.params.identifier },
              })
            "
          >
            <span class="ml-4 text-dark">{{ $gettext('Back to campsite') }}</span>
          </CiNavigationToggle>
        </div>
      </div>
      <client-only>
        <div
          v-if="!pending"
          class="container"
        >
          <form
            ref="form"
            novalidate
            @submit.prevent="onSubmit"
          >
            <div class="row mt-12">
              <div class="col-12">
                <h2>
                  {{ $gettext('Send inquiry to %{campsite}', { campsite: campsiteStore.campsite?.meta?.name }, true) }}
                </h2>
                <div class="row">
                  <div class="co-12 col-md-8">
                    <p class="mt-6">
                      {{ i18nInquireCopy }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-12 col-lg-6">
                <InputTextfield
                  id="firstName"
                  v-model="firstNameValue"
                  :label="$gettext('Firstname')"
                  :error="!!firstNameError"
                  type="text"
                  inverted
                  :disabled="submitPending"
                  :required="true"
                  name="firstName"
                />

                <InputTextfield
                  id="lastName"
                  v-model="lastNameValue"
                  :label="$gettext('Lastname')"
                  :error="!!lastNameError"
                  type="text"
                  inverted
                  :disabled="submitPending"
                  :required="true"
                  name="lastName"
                />

                <InputTextfield
                  id="email"
                  v-model="emailValue"
                  :label="$gettext('E-Mail')"
                  :error="!!emailError"
                  type="email"
                  inverted
                  :disabled="submitPending"
                  :required="true"
                  name="email"
                />

                <div class="date-of-stay">
                  <label class="input-label font-medium"><span>{{ $gettext('Travel period') }}</span></label>
                  <DatePicker
                    v-model.range="range"
                    :locale="locale"
                    color="green"
                    :initial-page="datePickerJumpDate"
                    :min-date="new Date()"
                    :masks="{ input: 'D. MMMM YYYY' }"
                    :update-on-input="false"
                    :columns="viewport.isLessThan('md') ? 1 : 2"
                    @update:model-value="onDateSelect"
                  >
                    <template #default="{ inputValue, togglePopover }">
                      <div
                        class="flex size-full flex-row items-center rounded p-4"
                        :class="[
                          submitPending
                            ? 'pointer-events-none cursor-not-allowed bg-gray-30'
                            : 'cursor-pointer bg-gray-10',
                        ]"
                        @click="togglePopover"
                      >
                        <CiAwesomeIcon
                          :icon="faCalendarDay"
                          class=""
                        />
                        <span
                          v-if="inputValue.start && inputValue.end"
                          class="ml-2"
                        >{{ inputValue.start }} - {{ inputValue.end }}</span>
                        <CiAwesomeIcon
                          v-if="range?.start || range?.end"
                          :icon="faTimes"
                          class="ml-auto"
                          @click.stop.prevent="() => onDateSelect(null)"
                        />
                      </div>
                    </template>
                  </DatePicker>
                </div>
              </div>
              <div class="col-12 col-lg-6 mt-6 lg:mt-0">
                <InputTextfield
                  id="message"
                  v-model="messageValue"
                  class="!flex h-full flex-col"
                  :placeholder="$gettext('Write your message here')"
                  :label="$gettext('Message')"
                  :error="!!messageError"
                  :disabled="submitPending"
                  type="textarea"
                  inverted
                  :required="true"
                  name="message"
                />
              </div>
              <div
                v-if="campsiteStore.campsite?.meta?.supported_languages?.length > 0"
                class="col-12 mt-6"
              >
                <label class="input-label block text-center font-medium">
                  <span>{{ $gettext('In which language should the campsite reply?') }}</span>
                </label>
                <div class="block md:flex md:justify-center">
                  <InputCheckbox
                    v-for="lang in campsiteStore.campsite.meta.supported_languages"
                    :id="lang"
                    :key="lang"
                    :value="inquire.desired_languages[lang]"
                    class="mb-2 mr-2 block md:inline"
                    :label="getLanguage(lang)"
                    :disabled="submitPending"
                    :is-inline="true"
                    @input="value => onLanguageSelected(value, lang)"
                  />
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-12">
                <span class="text-small-book"><sup>*</sup><span>{{ $gettext('Mandatory fields') }}</span></span>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-12">
                <p
                  v-html="
                    $gettext(
                      'Your request will be forwarded by us to the above-mentioned campsite for the purpose of contacting you directly (see %{privacyLink} and %{termsLink})',
                      { privacyLink: i18PrivacyPolicyLink, termsLink: i18nTermsOfUseLink },
                      true,
                    )
                  "
                />
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <InputCheckbox
                  id="marketingSignup"
                  :allow-html="true"
                  :label="i18nMarketingSignupLabel"
                  :value="marketingSignupValue"
                  name="marketingSignup"
                  :disabled="submitPending"
                  @input="onMarketingSignupClicked"
                />
              </div>
            </div>

            <div
              v-if="error"
              class="row mt-12"
            >
              <div class="col-12">
                <div
                  class="alert alert-danger"
                  role="alert"
                  v-html="error"
                />
              </div>
            </div>

            <div class="row my-12">
              <div class="col-12 col-md-6 col-lg-3 offset-lg-3 order-2 mt-4 md:order-1 md:mt-0">
                <button
                  class="button w-full"
                  :class="[submitPending ? 'button--disabled' : 'button--dark-outline']"
                  type="button"
                  @click="onCancel"
                >
                  {{ $gettext('Cancel') }}
                </button>
              </div>
              <div class="col-12 col-md-6 col-lg-3 order-1 md:order-2">
                <button
                  :disabled="submitPending"
                  class="button icon-absolute w-full"
                  :class="[submitPending ? 'button--disabled' : 'button--primary']"
                  type="button"
                  @click="onSubmit"
                >
                  <span class="mx-auto">{{ $gettext('Send') }}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
        <CiSkeletonInquire v-else />
        <template #placeholder>
          <CiSkeletonInquire />
        </template>
      </client-only>
    </template>
    <CiErrorView
      v-else
      :error="fetchStatus"
    />
  </div>
</template>

<style lang="postcss" scoped>
:deep(.vc-popover-content-wrapper) {
  @apply max-w-[90%] max-lg:min-w-[90%] md:max-w-xl;
}

:deep(.vc-container) {
  @apply w-full;
}
</style>
